import { getAuthHeader } from "./authHeader";
import axios from "axios";

import { CALLS_API as API_URL } from "../apiConstants";
import { requestErrorMessage } from "../utils/loadError";

// Helper function to handle errors consistently. `fallback` is the plain
// sentence a person reads when the server gives no usable reason.
const handleApiError = (error, fallback = "The call request did not go through.") => {
  // Validation text is kept; a 404 page, stack trace or axios status line is not.
  const wrapped = new Error(requestErrorMessage(error, fallback));
  wrapped.status = error.response?.status;
  throw wrapped;
};

const callApi = {
  /**
   * Fetches a list of calls.
   * @param {object} params - Query parameters (e.g., limit, page, status, agentId)
   * @returns {Promise<object>} - Promise resolving to { calls: [...], totalItems, totalPages, currentPage }
   */
  getAll: async (params = {}) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.get(API_URL, { params, ...authConfig });
      return (
        response.data || {
          calls: [],
          totalItems: 0,
          totalPages: 1,
          currentPage: 1,
        }
      );
    } catch (error) {
      handleApiError(error, "Calls could not be loaded.");
    }
  },

  /**
   * Fetches a single call by ID.
   * @param {string|number} id - The ID of the call.
   * @returns {Promise<object>} - Promise resolving to the call object.
   */
  getById: async (id) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.get(`${API_URL}/${id}`, authConfig);
      return response.data;
    } catch (error) {
      handleApiError(error, "This call could not be loaded.");
    }
  },

  /**
   * Updates call details (e.g., notes, outcome, duration, status).
   * @param {string|number} id - The ID of the call to update.
   * @param {object} callData - The updated data (e.g., { notes: '...', duration: 120, status: 'completed' }).
   * @returns {Promise<object>} - Promise resolving to the updated call object.
   */
  update: async (id, callData) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.put(
        `${API_URL}/${id}`,
        callData,
        authConfig,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "The call could not be updated.");
    }
  },

  /**
   * Saves call disposition data after a call ends.
   * @param {string|number} callId - The ID of the call.
   * @param {object} dispositionData - The disposition data (e.g., { outcome, notes, followUpDate }).
   * @returns {Promise<object>} - Promise resolving to the updated call object.
   */
  saveDisposition: async (callId, dispositionData) => {
    try {
      const authConfig = getAuthHeader();
      // The endpoint reads variables.workflow in camelCase (contactId,
      // followUpAt, ...). The global request interceptor snake-cases object
      // bodies, which made the backend drop every workflow field, so this
      // body is sent pre-serialized and reaches Phoenix as written.
      const response = await axios.put(
        `${API_URL}/${callId}/disposition`,
        JSON.stringify(dispositionData),
        authConfig,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "The outcome could not be saved.");
    }
  },

  /**
   * Fetches recent calls.
   * @param {object} params - Query parameters (e.g., limit, page).
   * @returns {Promise<object>} - Promise resolving to recent calls data.
   */
  getRecentCalls: async (params = {}) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.get(API_URL, {
        params: { ...params, recent: true },
        ...authConfig,
      });
      return (
        response.data || {
          calls: [],
          totalItems: 0,
          totalPages: 1,
          currentPage: 1,
        }
      );
    } catch (error) {
      handleApiError(error, "Recent calls could not be loaded.");
    }
  },

  complete: async (callId, callData = {}) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.put(
        `${API_URL}/${callId}/complete`,
        callData,
        authConfig,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "The call could not be completed.");
    }
  },

  /**
   * Starts a tracked companion call for an existing contact. The server derives
   * the destination and trunk from tenant-owned records.
   */
  initiate: async ({ contactId, customerId, campaignId } = {}) => {
    const canonicalContactId = contactId || customerId;
    if (!canonicalContactId) {
      throw new Error("A contact is required to start a tracked call.");
    }

    try {
      const authConfig = getAuthHeader();
      const response = await axios.post(
        API_URL,
        {
          call: {
            contactId: canonicalContactId,
            ...(campaignId ? { campaignId } : {}),
          },
        },
        authConfig,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Could not start the call.");
    }
  },
};

export default callApi;
